import {
  useEditor,
  EditorContent,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import type { FC } from "react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import { useDynamicRect } from "~/utils/dom/useAnimateSize";
import AppFloatMenu from "./AppFloatMenu";
import clsx from "clsx";
import AppBubbleMenu from "./AppBubbleMenu";
import ErrorDisplay from "../Common/ErrorDisplay";
import type { FieldMetadata } from "node_modules/@conform-to/react/context";
import type { CategoryItemType } from "~/database/category.model";
import CategoryModal from "./CategoryModal";
import EditorTitleInput from "./EditorTitleInput";
import Link from "@tiptap/extension-link";
import BulletList from "@tiptap/extension-bullet-list";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";

interface TiptapProps {
  setContent: (content: string) => void;
  setIsDirty: (isDirty: boolean) => void;

  //json
  content: string;
  title: FieldMetadata;
  titleInputControl: any;
  overview: FieldMetadata;
  overviewInputControl: any;
  categories: CategoryItemType[];
  setCategories: (
    categories: CategoryItemType[],
  ) => void;
  isStory?: FieldMetadata;
  isStoryInputControl?: any;
}

export const EditerTiptap = [
  StarterKit,
  Image,
  BulletList,
  OrderedList,
  ListItem,

  Placeholder.configure({
    placeholder: "PR本文",
    emptyEditorClass: "is-editor-empty",
    emptyNodeClass: "is-empty",
    showOnlyWhenEditable: true,
    showOnlyCurrent: true,
  }),
  Link.configure({
    openOnClick: false,
    autolink: true,
    defaultProtocol: "https",
  }),
];

const Tiptap: FC<TiptapProps> = ({
  content,
  setContent,
  title,
  titleInputControl,
  overview,
  overviewInputControl,
  categories,
  setIsDirty,
  setCategories,
  isStory,
  isStoryInputControl,
}) => {
  const editor = useEditor({
    extensions: EditerTiptap,
    content: content ? JSON.parse(content) : "",

    /* content:
      '<font color="#3b3b3b">近年、女性向け美容商材の広告に男性タレントが起用されるなど「ジェンダーに捉われることのない美容」に注目が集まっています。「メイクは女性がするもの」という価値観は今や過去のものとなり、大手美容メーカー各社もメンズメイクやスキンケアの商品ラインナップ拡充に力を入れるなど、</font><strong><font color="#3b3b3b">美容市場の中でも特に盛り上がりを見せているのが男性向け美容商材</font></strong><font color="#3b3b3b">です。</font>\n<br><font color="#3b3b3b">しかし、メンズ美容に関する消費者の実態を明らかにした情報はまだ世の中に少なく、その動向が分かるような定点調査もほとんど行われていません。</font><br><br><font color="#3b3b3b">そこで、「メンズ美容総研」では、15～59歳の男性を対象とした、男性の美意識や美容への取り組みの実態を定点で追う「メンズ美容定点調査」を開始。その第1回目の調査結果を発表いたします。</font><br><br><font color="#3b3b3b">=====================================================</font><br><strong><font color="#3b3b3b"><u>調査概要</u></font></strong><br><font color="#3b3b3b">調査対象：15～59歳 男性2,000名 ※年代均等割付</font><br><font color="#3b3b3b">調査期間：2022年1月24日（月）～1月25日（火）</font><br><font color="#3b3b3b">調査方法：インターネット調査</font><br><font color="#3b3b3b">※調査結果の数値は小数点第二位を四捨五入しており、合計値が100%にならない場合もあります。</font><br><font color="#3b3b3b">=====================================================</font><br><br><font color="#3b3b3b">=====================================================</font><br><strong><font color="#3b3b3b"><u>調査サマリー</u></font></strong><br><font color="#3b3b3b">◆ 若い年代ほど「スキンケア」「メンズメイク」に強い関心</font><br><font color="#3b3b3b">◆ スキンケアに取り組む人は66.5%、メイクに取り組む人は14.6%と差が開く結果に</font><br><font color="#3b3b3b">◆ メンズメイクは世間になじんだ!? 半数近くが「世間に受け入れられている」</font><br><font color="#3b3b3b">◆ 美容商品の購入場所はECサイト54.2%、店頭75.0%…かける金額は月平均2,588円</font><br><font color="#3b3b3b">=====================================================</font><br><font color="#3b3b3b">&nbsp;</font><br><ul><li style="text-align:left;"><strong><u>若い年代ほど「スキンケア」「メンズメイク」に強い関心</u></strong><br></li></ul><br><font color="#3b3b3b">まず、「自分は、同世代の平均的な男性に比べて、スキンケアやメンズメイクに関心が強いと思うか」を聞いたところ、「スキンケア」「メンズメイク」ともに、年齢層が低いほど「関心が強いと思う」と答える方の割合は多いことが分かりました。</font><br><br><font color="#3b3b3b">具体的に、「スキンケアへの関心が強い」人の割合は10代：38.0%、20代：35.3%、30代：30.0%、40代：21.3%、50代：14.5%。「メンズメイクへの関心が強い」人の割合は10代：18.3%、20代：17.8%、30代：10.5%、40代：8.5%、50代：4.0%となっています。</font><br><img src="https://prcdn.freetls.fastly.net/release_image/56551/91/56551-91-d6933593a9119d44bb904d712985fbb6-3900x2048.jpg?format=jpeg&amp;auto=webp&amp;quality=85%2C65&amp;width=1950&amp;height=1350&amp;fit=bounds"><br><font color="#3b3b3b">&nbsp;</font><br><ul><li style="text-align:left;"><strong><u>スキンケアに取り組む人は66.5%、メイクに取り組む人は14.6%と差が開く結果に</u></strong><br></li></ul><br><font color="#3b3b3b">また、「あなたは日ごろ、スキンケアをしていますか」という質問では、「ほぼ毎日おこなっている」が36.3％、「たまにおこなっている」が30.3%となり、合わせると66.5%の方がスキンケアに取り組んでいるという結果に。中でも20代は81.0％、10代は77.3%の方がスキンケアに取り組んでおり、若い男性はスキンケアが定着していると言えそうです。</font><br><br><font color="#3b3b3b">一方で、「メイク」をしている人の割合は「ほぼ毎日行っている」が4.4%、「たまにおこなっている」が10.2%で、合わせて14.6%に留まっています。スキンケアと比べると、まだまだメンズメイクに取り組めている人は多くないようです。</font><br><img src="https://prcdn.freetls.fastly.net/release_image/56551/91/56551-91-b6a6573bf94287d82de3a44c5b4fe910-3900x2048.jpg?format=jpeg&amp;auto=webp&amp;quality=85%2C65&amp;width=1950&amp;height=1350&amp;fit=bounds"><br><font color="#3b3b3b">&nbsp;</font><br><ul><li style="text-align:left;"><strong><u>メンズメイクは世間になじんだ!? 半数近くが「世間に受け入れられている」</u></strong><br></li></ul><br><font color="#3b3b3b">次に、「世間一般的に『男性がメイク(※)すること』は受け入れられていると思うか」という質問では、「受け入れられている」と答えた方は全体で44.4%。最も多かったのは10代で54.3%となりました。10代の男性は、スキンケアやメンズメイクへのハードルの高さを感じていないからこそ、関心も強くなる傾向にあると考えられそうです。一方で「受け入れられている」と答えた方が最も少なかったのは30代で37.3%となりました。40代や50代よりスキンケアやメンズメイクへの関心が強い30代ですが、年上・年下どちらとも接することの多い「中間管理職」のような年代であると考えられ、世代間のギャップを感じる機会も多いのかもしれません。</font><br><br><font color="#3b3b3b">また、いずれの年代でも関心度や実施率に比べて「受け入れられている」と感じている人の割合は多く、「メンズメイクは普通のことである」という世の中の流れは少なからずあると考えられます。</font><br><br><font color="#3b3b3b">※「メイク」は「スキンケア（化粧水や乳液）」は含まず、ファンデーションや口紅、アイシャドウ等を使用した「メーキャップ」を指します。</font><br><img src="https://prcdn.freetls.fastly.net/release_image/56551/91/56551-91-cd2618fc12b2e347937dc0604744018d-3900x2048.jpg?format=jpeg&amp;auto=webp&amp;quality=85%2C65&amp;width=1950&amp;height=1350&amp;fit=bounds"><br><font color="#3b3b3b">&nbsp;</font><br><ul><li style="text-align:left;"><strong><u>美容商品の購入場所はECサイト54.2%、店頭75.0%…かける金額は月平均2,588円</u></strong><br></li></ul><br><font color="#3b3b3b">続いて、美容商品を購入する場所について調査。「美容商品を購入することがある」（63.5%）人のうち、「ECサイトで購入している」と答えた方は54.2%、「店頭で購入している」と答えた方は75.0%となりました。半数以上の方がECサイトを活用している一方で、店頭での購入も高い需要があることが分かります。</font><br><img src="https://prcdn.freetls.fastly.net/release_image/56551/91/56551-91-c33f9a6a410f93ac8cd07f830680bb76-3900x2048.jpg?format=jpeg&amp;auto=webp&amp;quality=85%2C65&amp;width=1950&amp;height=1350&amp;fit=bounds"><br><br><font color="#3b3b3b">最後に、美容商品を購入することがある方に「毎月、スキンケアアイテムやコスメにどの程度お金をかけているか」を聞いたところ、最もお金をかけているのは20代で平均3,331円、最もお金をかけていないのは50代で平均1,974円、全体の平均は2,588円となりました。</font><br><img src="https://prcdn.freetls.fastly.net/release_image/56551/91/56551-91-101db0c98d859a252b326f3b26a5dccf-3900x2048.jpg?format=jpeg&amp;auto=webp&amp;quality=85%2C65&amp;width=1950&amp;height=1350&amp;fit=bounds"><br><br><font color="#3b3b3b">&nbsp;</font><br><strong><font color="#3b3b3b"><u>◆ 「Soft Drink」について</u></font></strong><br><br><font color="#3b3b3b">「Soft Drink」では、芸能活動・インフルエンサーとしての活動や、商品・ブランド開発などに興味がある美容感度の高い男性をKOL・インフルエンサーとしてマネジメントいたします。</font><br><font color="#3b3b3b">KOLを活用したクリエイティブの制作から、マーケティング施策のデータ分析まで幅広くカバーすることで、クライアントのニーズに合ったマーケティングを提供します。</font><br><br><font color="#3b3b3b">Soft Drink公式ホームページ：</font><font color="#358cda"><a href="https://soft-drink.jp/" target="_blank">https://soft-drink.jp/</a></font><br><font color="#3b3b3b">Soft Drink公式Instagram：</font><font color="#358cda"><a href="https://www.instagram.com/soft_drink_official/" target="_blank">https://www.instagram.com/soft_drink_official/</a></font><br><br><br><strong><font color="#3b3b3b"><u>◆ メンズ美容コミュニティ会員「Soft Drink Member」を募集中！</u></font></strong><br><br><font color="#3b3b3b">「Soft Drink」では、所属タレントの他に、メンズ美容を一緒に盛り上げてくれるコミュニティ会員「Soft Drink Member」を募集中です。</font><br><font color="#3b3b3b">「Soft Drink Member」になると、新作コスメのモニター企画へのオファーや、Soft Drink主催イベントへの優先招待、各メディアやSNS投稿企画への参加チャンスなど、さまざまな特典がございます。</font><br><font color="#3b3b3b">「周りを気にすることなく『自分のありのまま』を表現したい」「メンズ美容を盛り上げたい」という想いをお持ちの方は、ぜひご応募ください。</font><br><br><font color="#3b3b3b">「Soft Drink Member」募集ページ：</font><font color="#358cda"><a href="https://tpjb.f.msgs.jp/webapp/form/24069_tpjb_1/index.do" target="_blank">https://tpjb.f.msgs.jp/webapp/form/24069_tpjb_1/index.do</a></font>', */
  });

  const save = useCallback(() => {
    if (editor) {
      const json = editor.getJSON();
      setContent(JSON.stringify(json));
    }
  }, [editor, setContent]);

  useEffect(() => {
    if (editor) {
      editor.on("update", () => {
        const json = editor.getJSON();
        setContent(JSON.stringify(json));
        setIsDirty(true);
      });
      return () => {
        editor.off("update");
      };
    }
  }, [editor, setContent, setIsDirty]);

  const { rect, observeRef } = useDynamicRect([
    editor,
  ]);
  const titleRef =
    useRef<HTMLTextAreaElement>(null);
  const overviewRef =
    useRef<HTMLTextAreaElement>(null);

  const focusTitleAtEnd = useCallback(() => {
    if (titleRef.current) {
      titleRef.current.focus();
      const length =
        titleRef.current.value.length;
      titleRef.current.setSelectionRange(
        length,
        length,
      );
    }
  }, []);

  const focusOverviewAtEnd = useCallback(() => {
    if (overviewRef.current) {
      overviewRef.current.focus();
      const length =
        overviewRef.current.value.length;
      overviewRef.current.setSelectionRange(
        length,
        length,
      );
    }
  }, []);
  const [isModalOpen, setIsModalOpen] =
    useState(false);
  if (!editor) {
    return null;
  }
  return (
    <div
      className={clsx("mt-48", "max-md:mt-20")}
    >
      {isModalOpen && (
        <CategoryModal
          categories={categories}
          setCategories={(el) => {
            setIsDirty(true);
            setCategories(el);
          }}
          setIsModalOpen={setIsModalOpen}
        />
      )}

      <div
        className={clsx(
          "flex",
          "flex-col",
          "gap-y-20",
        )}
      >
        <div
          className={clsx(
            "text-14",
            "text-gray-400",
            "flex",
            "items-center",
            "gap-x-20",
            "gap-y-5",
            "max-md:text-12",
            "flex-wrap",
          )}
        >
          カテゴリー:
          {categories.map((el) => {
            return (
              <div
                className={clsx(
                  "flex",
                  "items-center",
                  "gap-10",
                )}
                key={el.id}
              >
                {el.name}
              </div>
            );
          })}
          <div
            className={clsx(
              "border",
              "border-black",
              "rounded-full",
              "text-black",
              "px-20",
              "py-1",
              "relative",
              "cursor-pointer",
              "text-12",
            )}
            onClick={() => {
              setIsModalOpen(true);
            }}
          >
            + カテゴリーを追加
          </div>
        </div>

        {isStory && isStoryInputControl && (
          <div
            className={clsx(
              "flex",
              "items-center",
              "gap-8",
              "text-14",
              "max-md:text-12",
            )}
          >
            <input
              type="checkbox"
              id="isStory"
              name="isStory"
              defaultChecked={
                isStory.value === "true"
              }
              onChange={(e) => {
                setIsDirty(true);
                isStoryInputControl.change(
                  e.target.checked
                    ? "true"
                    : "false",
                );
              }}
              className={clsx(
                "w-16",
                "h-16",
                "cursor-pointer",
              )}
            />
            <label
              htmlFor="isStory"
              className={clsx("cursor-pointer")}
            >
              STORY記事として公開
            </label>
          </div>
        )}
      </div>

      <div className={clsx("mt-20")}>
        <div
          className={clsx("mt-5", "text-red-700")}
        >
          <ErrorDisplay
            error={{
              validation: title.errors ?? [],
            }}
          ></ErrorDisplay>
        </div>

        <div className={clsx("mt-16")}>
          <EditorTitleInput
            setIsDirty={setIsDirty}
            isTitle={true}
            title={title}
            titleInputControl={titleInputControl}
            goNext={() => {
              /* overviewRef.current?.setSelectionRange(
                0,
                0,
              );
              overviewRef.current?.focus(); */

              editor
                ?.chain()
                .focus()
                .setParagraph()
                .run();
            }}
            titleRef={titleRef}
          ></EditorTitleInput>
        </div>

        {/*   <div
          className={clsx("mt-5", "text-red-700")}
        >
          <ErrorDisplay
            error={{
              validation: overview.errors ?? [],
            }}
          ></ErrorDisplay>
        </div>

        <div className={clsx("mt-16")}>
          <EditorTitleInput
            setIsDirty={setIsDirty}
            title={overview}
            titleInputControl={
              overviewInputControl
            }
            goPrev={() => {
              focusTitleAtEnd();
            }}
            goNext={() => {
              editor
                ?.chain()
                .focus()
                .setParagraph()
                .run();
            }}
            titleRef={overviewRef}
          ></EditorTitleInput>
        </div> */}
      </div>

      <div className={"tiptapEditor"}>
        <div className="min-h-400">
          <div
            className="mt-12 p-12"
            ref={observeRef}
          >
            <EditorContent
              editor={editor}
              onKeyDown={(event) => {
                if (
                  (event.key === "Backspace" ||
                    event.key === "ArrowLeft") &&
                  editor?.state.selection.$anchor
                    .pos === 1
                  //&&editor?.state.doc.textContent.trim() === ""
                ) {
                  event.preventDefault();
                  //focusOverviewAtEnd();
                  focusTitleAtEnd();
                }
              }}
            />
            {editor && (
              <AppBubbleMenu
                editor={editor}
                setContent={save}
              ></AppBubbleMenu>
            )}
            {editor && (
              <AppFloatMenu
                editor={editor}
                rect={rect}
                setContent={save}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Tiptap;
